<!-- Toolbar: search · count · refresh · add -->
<div class="flex flex-wrap items-center gap-2">
  <div class="relative min-w-0 flex-1 basis-56">
    <i class="pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-(--p-text-muted-color)" aria-hidden="true"></i>
    <input
      pInputText
      type="search"
      class="w-full !min-h-11 !pl-9 sm:!min-h-10"
      [placeholder]="'Search ' + (isSpace ? 'Space tags' : 'my private tags') + '…'"
      [attr.aria-label]="'Search ' + (isSpace ? 'Space tags' : 'private tags')"
      [(ngModel)]="search"
      autocomplete="off" />
  </div>
  <span class="text-xs text-(--p-text-muted-color)" aria-live="polite">
    @if (search) { {{ visibleTags.length }} of } {{ tags.length }} {{ tags.length === 1 ? 'tag' : 'tags' }}
  </span>
  <p-button
    icon="pi pi-refresh"
    [text]="true"
    [rounded]="true"
    [loading]="loading"
    (onClick)="load()"
    ariaLabel="Refresh tags"
    pTooltip="Refresh"
    tooltipPosition="top"
    styleClass="!h-11 !w-11 sm:!h-10 sm:!w-10" />
  @if (canEdit) {
    <p-button
      [label]="isSpace ? 'Add Space tag' : 'Add private tag'"
      icon="pi pi-plus"
      (onClick)="openCreate()"
      styleClass="!min-h-11 sm:!min-h-10" />
  }
</div>

@if (loading && !tags.length) {
  <div class="mt-4 space-y-2" aria-busy="true">
    @for (row of [1, 2, 3]; track row) {
      <div class="h-11 animate-pulse rounded-lg bg-(--p-content-hover-background)"></div>
    }
  </div>
} @else if (loadError) {
  <div class="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" role="alert">
    {{ loadError }}
  </div>
} @else if (!tags.length) {
  <div class="mt-4 flex flex-col items-center gap-2 rounded-xl border border-dashed border-(--p-content-border-color) px-6 py-10 text-center">
    <span class="material-symbols-outlined !text-4xl text-(--p-text-muted-color)" aria-hidden="true">sell</span>
    <p class="font-semibold">{{ isSpace ? 'This Space has no tags yet' : 'You have no private tags yet' }}</p>
    <p class="max-w-sm text-sm text-(--p-text-muted-color)">
      {{ isSpace
        ? 'Space tags are shared with every member of this Space and can be used on its notes.'
        : 'Private tags are visible only to you and can be used on any note you can open.' }}
    </p>
  </div>
} @else {
  <div class="mt-3 overflow-x-auto rounded-xl border border-(--p-content-border-color)">
    <table class="w-full border-collapse text-sm">
      <thead class="bg-(--p-content-hover-background) text-left text-xs uppercase tracking-wide text-(--p-text-muted-color)">
        <tr>
          <th class="px-3 py-2 font-semibold">
            <button type="button" class="inline-flex min-h-8 items-center gap-1" (click)="toggleSort('name')">
              Name <span class="material-icons !text-[0.8rem]" aria-hidden="true">{{ sortIcon('name') }}</span>
            </button>
          </th>
          <th class="hidden px-3 py-2 font-semibold sm:table-cell">
            <button type="button" class="inline-flex min-h-8 items-center gap-1" (click)="toggleSort('type')">
              Type <span class="material-icons !text-[0.8rem]" aria-hidden="true">{{ sortIcon('type') }}</span>
            </button>
          </th>
          <th class="hidden px-3 py-2 font-semibold lg:table-cell">Description</th>
          <th class="px-3 py-2 text-center font-semibold">
            <button type="button" class="inline-flex min-h-8 items-center gap-1" (click)="toggleSort('usedIn')"
              [pTooltip]="isSpace ? 'Notes in this Space using the tag' : 'Your notes with this private tag'" tooltipPosition="top">
              Used <span class="material-icons !text-[0.8rem]" aria-hidden="true">{{ sortIcon('usedIn') }}</span>
            </button>
          </th>
          <th class="hidden px-3 py-2 text-center font-semibold sm:table-cell">Active</th>
          @if (canEdit) {
            <th class="px-3 py-2 text-right font-semibold"><span class="sr-only">Actions</span></th>
          }
        </tr>
      </thead>
      <tbody>
        @for (tag of visibleTags; track tag._id) {
          <tr class="border-t border-(--p-content-border-color) hover:bg-(--p-content-hover-background)" [class.opacity-60]="tag.isActive === false">
            <td class="px-3 py-1.5">
              <button
                type="button"
                class="inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-full border border-(--p-content-border-color) px-2.5 py-0.5 text-left"
                [ngStyle]="chipStyle(tag)"
                [disabled]="!canEdit"
                (click)="openEdit(tag)"
                [attr.aria-label]="canEdit ? 'Edit tag ' + tag.name : tag.name">
                <span class="truncate">{{ tag.name }}</span>
              </button>
              <div class="mt-0.5 truncate text-xs text-(--p-text-muted-color) sm:hidden">
                {{ tag.tagType }}@if (tag.isActive === false) { · inactive }
              </div>
            </td>
            <td class="hidden px-3 py-1.5 text-(--p-text-muted-color) sm:table-cell">
              @if (tag.tagType === 'calendar') {
                <span class="inline-flex items-center gap-1"><span class="material-symbols-outlined !text-base" aria-hidden="true">calendar_month</span>calendar</span>
              } @else {
                {{ tag.tagType || '—' }}
              }
            </td>
            <td class="hidden max-w-[22rem] truncate px-3 py-1.5 text-(--p-text-muted-color) lg:table-cell" [title]="tag.description || ''">
              {{ tag.description || '—' }}
            </td>
            <td class="px-3 py-1.5 text-center tabular-nums">{{ usageLabel(tag._id) }}</td>
            <td class="hidden px-3 py-1.5 text-center sm:table-cell">
              <span class="material-icons !text-base" [ngClass]="tag.isActive !== false ? 'text-emerald-500' : 'text-(--p-text-muted-color)'"
                [attr.aria-label]="tag.isActive !== false ? 'Active' : 'Inactive'">
                {{ tag.isActive !== false ? 'check_circle' : 'radio_button_unchecked' }}
              </span>
            </td>
            @if (canEdit) {
              <td class="px-1 py-1 text-right whitespace-nowrap">
                <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" (onClick)="openEdit(tag)"
                  [ariaLabel]="'Edit tag ' + tag.name" pTooltip="Edit" tooltipPosition="top"
                  styleClass="!h-11 !w-11 sm:!h-9 sm:!w-9" />
                <span [pTooltip]="deleteTooltip(tag)" tooltipPosition="top">
                  <p-button icon="pi pi-trash" [text]="true" [rounded]="true" severity="danger"
                    [disabled]="!canDelete(tag) || deletingId === tag._id" [loading]="deletingId === tag._id"
                    (onClick)="confirmDelete(tag)" [ariaLabel]="'Delete tag ' + tag.name"
                    styleClass="!h-11 !w-11 sm:!h-9 sm:!w-9" />
                </span>
              </td>
            }
          </tr>
        } @empty {
          <tr><td class="px-3 py-6 text-center text-(--p-text-muted-color)" colspan="6">No tags match "{{ search }}".</td></tr>
        }
      </tbody>
    </table>
  </div>
  @if (!usage) {
    <p class="mt-2 text-xs text-(--p-text-muted-color)">Usage counts are unavailable right now.</p>
  }
}

<!-- Create / edit dialog (same for private and Space tags) -->
<p-dialog
  [(visible)]="dialogVisible"
  (onHide)="closeDialog()"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="true"
  [style]="{ width: 'min(36rem, 95vw)' }"
  [header]="(editing ? 'Edit ' : 'New ') + noun"
  appendTo="body">
  <form class="flex flex-col gap-4" (ngSubmit)="save()">
    <p class="text-xs text-(--p-text-muted-color)">
      {{ isSpace ? 'Shared with every member of this Space.' : 'Visible only to you.' }}
    </p>
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium" for="tm-name">Name *</label>
      <input pInputText id="tm-name" name="name" class="w-full" [(ngModel)]="form.name" autocomplete="off" required />
      @if (duplicateName) {
        <small class="text-red-600 dark:text-red-400">A {{ noun }} with this name already exists.</small>
      }
    </div>
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium" for="tm-type">Type</label>
        <p-select inputId="tm-type" name="tagType" [options]="tagTypeOptions" optionLabel="label" optionValue="value"
          [(ngModel)]="form.tagType" appendTo="body" styleClass="w-full" />
        @if (form.tagType === 'calendar') {
          <small class="text-(--p-text-muted-color)">Notes with this tag appear in the Calendar view.</small>
        }
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium" for="tm-slug">Slug</label>
        <input pInputText id="tm-slug" name="slug" class="w-full font-mono text-sm" [(ngModel)]="form.slug"
          placeholder="generated from the name" autocomplete="off" />
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium" for="tm-description">Description</label>
      <textarea pTextarea id="tm-description" name="description" class="w-full" rows="2" [autoResize]="true"
        [(ngModel)]="form.description"></textarea>
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium" for="tm-color">Color</label>
      <div class="flex items-center gap-2">
        <p-colorPicker inputId="tm-color" name="colorPicker" format="hex" [ngModel]="form.color" (onChange)="setColor($event.value)" appendTo="body" />
        <input pInputText name="color" class="w-32 font-mono text-sm" [ngModel]="form.color" (ngModelChange)="setColor($event)"
          placeholder="#RRGGBB" aria-label="Color hex value" autocomplete="off" />
        @if (form.color) {
          <p-button icon="pi pi-times" [text]="true" [rounded]="true" (onClick)="form.color = ''" ariaLabel="Clear color" />
        }
      </div>
      <div class="flex flex-wrap gap-1.5">
        @for (preset of commonColors; track preset.value) {
          <button type="button" class="h-7 w-7 rounded-full border-2 sm:h-6 sm:w-6"
            [ngClass]="isPresetColor(preset.value) ? 'border-(--p-text-color)' : 'border-transparent'"
            [style.background-color]="preset.value" [attr.aria-label]="preset.name" [title]="preset.name"
            (click)="form.color = preset.value"></button>
        }
      </div>
    </div>
    <label class="flex items-center gap-3">
      <p-toggleswitch name="isActive" [(ngModel)]="form.isActive" />
      <span class="text-sm">{{ form.isActive ? 'Active — offered when tagging notes' : 'Inactive — kept on notes, no longer offered' }}</span>
    </label>
    <div class="flex justify-end gap-2 border-t border-(--p-content-border-color) pt-3">
      <p-button label="Cancel" [text]="true" (onClick)="closeDialog()" />
      <p-button type="submit" [label]="editing ? 'Save changes' : 'Create tag'" icon="pi pi-check"
        [loading]="saving" [disabled]="!form.name.trim() || duplicateName || saving" />
    </div>
  </form>
</p-dialog>

<p-confirmDialog appendTo="body" />
